// ABOUTME: Toast notification for achievement unlocks
// ABOUTME: Shows at bottom of screen and auto-dismisses

import { TrophyIcon } from '@heroicons/react/24/outline'
import { useCallback, useEffect } from 'react'

interface AchievementToastProps {
  title: string
  description?: string
  icon?: React.ReactNode
  isActive: boolean
  onComplete: () => void
  autoDismissMs?: number
}

export function AchievementToast({
  title,
  description,
  icon,
  isActive,
  onComplete,
  autoDismissMs = 3000,
}: AchievementToastProps) {
  useEffect(() => {
    if (!isActive) return

    const timer = setTimeout(onComplete, autoDismissMs)
    return () => clearTimeout(timer)
  }, [isActive, onComplete, autoDismissMs])

  const handleDismiss = useCallback(() => {
    onComplete()
  }, [onComplete])

  if (!isActive) return null

  return (
    <button
      type="button"
      className="fixed bottom-24 left-4 right-4 z-50 mx-auto max-w-sm"
      onClick={handleDismiss}
      aria-label="Dismiss achievement"
    >
      <div className="relative overflow-hidden rounded-lg bg-surface-elevated border-l-4 border-editorial-gold shadow-[0_4px_24px_rgba(0,0,0,0.15)]">
        {/* Gold accent border */}
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-editorial-gold" />

        <div className="flex items-center gap-4 p-4 pl-5">
          {/* Icon */}
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-editorial-gold flex items-center justify-center shadow-lg">
            {icon || <TrophyIcon className="w-6 h-6 text-white" />}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-[var(--color-accent)] uppercase tracking-wider">
              Achievement Unlocked!
            </p>
            <h3 className="text-base font-semibold text-[var(--color-fg-primary)] truncate uppercase tracking-wider">
              {title}
            </h3>
            {description && (
              <p className="text-sm text-[var(--color-fg-secondary)] truncate">{description}</p>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}
